// The place card's booking button (WS-49 T5, contract section 14). Foundation only: the one provider is `link`.
// The action is resolved by the settings package (unit, then unit type, then place, then the map's default, then
// today's content `link`) and its address is built there with the URL API: https only, never joined from strings.
import { bookingAction, buildLink, t } from './settings/index.js';
import { html, raw } from './dom.js';
import { track } from './track.js';

/** @typedef {import('./settings/typedefs.js').Runtime} Runtime */
/**
 * What the card may pass as the second argument of bookingButton() and renderGallery(). All of it is optional.
 * @typedef {Object} CardContext
 * @property {Pick<Runtime, 'get' | 'on'>} [runtime]  default: `window.resortMap.runtime` (contract section 4)
 * @property {(key: string) => string} [ui]  interface-string lookup (web/i18n); default: the English texts below
 */

// Used until the chrome hands in its interface strings (`ui`), and for a key its language file lacks.
const EN = {
  'book.check': 'Book now', 'book.more': 'Discover more',
  'gallery.open': 'View {n} photos', 'gallery.close': 'Close', 'gallery.prev': 'Previous photo', 'gallery.next': 'Next photo',
};
const ARROW = raw('<svg class="ic " viewBox="0 0 24 24" aria-hidden="true"><path d="M7 17 17 7M9 7h8v8"/></svg>'); // ui.js icon('arrow')

/**
 * The live values a card part needs, read at render time: settings, the language on screen, interface strings.
 * @param {CardContext} [ctx]
 */
export function cardContext(ctx) {
  const runtime = ctx?.runtime ?? globalThis.resortMap?.runtime ?? null;
  const get = (key) => { try { return runtime?.get?.(key); } catch { return undefined; } }; // errors:ignore: a card part renders with defaults
  const fallback = get('lang.default') || 'en';
  const ui = (key) => {
    const s = ctx?.ui?.(key);
    return typeof s === 'string' && s && s !== key ? s : EN[key]; // a key name is never shown to a visitor
  };
  return { runtime, get, lang: get('lang.current') || fallback, fallback, ui };
}

/**
 * What the booking button of a card does, or null when the card has none: no address that may be opened, or
 * `booking.show` is off for this preset or placement.
 * @param {object} place  the card data of placeForKey(key)
 * @param {ReturnType<typeof cardContext>} c
 * @returns {{ href: string, label: string, target: string, provider: 'link', mode: 'link' } | null}
 */
export function resolveBooking(place, c) {
  if (!place?.id || c.get('booking.show') === false) return null;
  const target = place.unit ? { unit: place.id, type: place.unit, link: place.link } : { place: place.id, link: place.link };
  const action = bookingAction(target, c.get('booking.actions'));
  const href = buildLink(action, { lang: c.lang }); // every provider opens the link in this delivery
  if (!href) return null;
  const label = t(action.label, c.lang, c.fallback) || c.ui(place.unit ? 'book.check' : 'book.more');
  return { href, label, target: action.target, provider: 'link', mode: 'link' };
}

/**
 * The booking button for a place card, or null when there is nothing to show. It opens in a new tab and counts
 * one `booking_click` per press. Same element as the card's link on main (`a.cta`).
 * @param {object} place  the card data of placeForKey(key)
 * @param {CardContext} [ctx]
 * @returns {HTMLElement | null}
 */
export function bookingButton(place, ctx) {
  const b = resolveBooking(place, cardContext(ctx));
  if (!b) return null;
  const a = document.createElement('a');
  a.className = 'cta';
  a.href = b.href;
  a.target = '_blank';
  a.rel = 'noopener';
  a.innerHTML = html`<span>${b.label}</span><i>${ARROW}</i>`;
  const press = () => track('booking_click', { target: b.target, provider: b.provider, mode: b.mode });
  a.addEventListener('click', press);
  a.addEventListener('auxclick', (e) => { if (e.button === 1) press(); }); // a middle click opens the link too
  return a;
}
